import React, { useState, useEffect } from "react";
import { GoQuestion } from "react-icons/go";
import { RxUpload } from "react-icons/rx";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { AiOutlineDelete } from "react-icons/ai";
import { IoAddCircleOutline } from "react-icons/io5";
import { getCampaigns, createCampaign, uploadAudio, fetchCsrfToken, getAccountInfo } from "../../services/api";
import CreateCampaignModal from "../shared/components/CreateCampaignModal";
import API_BASE_URL from "../../config";
import Modal from "../shared/components/Modal";
import Spinner from "../shared/components/Spinner";
import UpgradeButton from "../../components/UpgradeButton";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasStripeSubscription, setHasStripeSubscription] = useState(null); // null = loading, true = has subscription, false = no subscription
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch account info to check Stripe subscription status
  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const accountInfo = await getAccountInfo();
        setHasStripeSubscription(accountInfo.hasStripeSubscription || false);
      } catch (error) {
        console.error('Error fetching account info:', error);
        setHasStripeSubscription(false);
      }
    };

    fetchAccountInfo();
  }, []);

  const fetchCampaigns = async () => {
    const toastId = toast.loading("Loading campaigns...");
    try {
      setLoading(true);
      const data = await getCampaigns();
      if (data && data.campaigns) {
        // Sort campaigns by creation date (newest first)
        const sortedCampaigns = [...data.campaigns].sort((a, b) => {
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
        setCampaigns(sortedCampaigns);
        
        // Get campaign ID from URL query parameter
        const params = new URLSearchParams(location.search);
        const campaignId = params.get('campaign');
        
        // If we have a campaign ID in the URL and it exists in our campaigns list,
        // select it
        if (campaignId && sortedCampaigns.some(c => c.id.toString() === campaignId)) {
          setSelectedCampaign(campaignId);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      toast.dismiss(toastId);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [location]); // Added location as dependency since we use it in fetchCampaigns

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleCreateCampaign = async (formData) => {
    const toastId = toast.loading("Creating campaign...");
    try {
      setLoading(true);
      const result = await createCampaign(formData.name);
      if (result.success) {
        await fetchCampaigns();
        setSelectedCampaign(result.campaign_id);
        toast.success("Campaign created successfully");
        setIsModalOpen(false);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      toast.dismiss(toastId);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('File selected:', file);
      // Check file size (limit to 1GB)
      if (file.size > 1024 * 1024 * 1024) {
        toast.error("File size must be less than 1GB");
        return;
      }
      
      // Check file type
      const allowedTypes = [
        'audio/3gpp', 'audio/3gpp2', 'audio/aac', 'audio/ac3', 'audio/aiff', 'audio/x-aiff',
        'audio/alac', 'audio/amr', 'audio/ape', 'audio/basic', 'audio/dss', 'audio/flac',
        'audio/x-flac', 'audio/x-m4a', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/opus',
        'audio/qcp', 'audio/tta', 'audio/voc', 'audio/wav', 'audio/x-ms-wma', 'audio/wv',
        'audio/webm', 'audio/mp3', 'audio/m4a', 'audio/m4b', 'audio/m4p', 'audio/m4r',
        'audio/mpga', 'audio/ogg', 'audio/oga', 'audio/opus', 'audio/qcp', 'audio/tta',
        'audio/voc', 'audio/wav', 'audio/wma', 'audio/wv',
        'video/webm', 'video/mts', 'video/m2ts', 'video/ts', 'video/mov', 'video/mp2',
        'video/mp4', 'video/m4p', 'video/m4v', 'video/mxf'
      ];
      if (!allowedTypes.includes(file.type) && !(file.type === 'audio/quicktime') && !(file.type === 'video/quicktime')) {
        toast.error("Please upload an supported audio file format");
        toast.error(file.type);
        return;
      }

      setUploadedFile(file);
      setUploadProgress(0); // Reset progress
    } else {
      console.log('No file selected.');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      // Check file size (limit to 1GB)
      if (file.size > 1024 * 1024 * 1024) {
        toast.error("File size must be less than 1GB");
        return;
      }

      // Check file type
      const allowedTypes = [
        'audio/3gpp', 'audio/3gpp2', 'audio/aac', 'audio/ac3', 'audio/aiff', 'audio/x-aiff',
        'audio/alac', 'audio/amr', 'audio/ape', 'audio/basic', 'audio/dss', 'audio/flac',
        'audio/x-flac', 'audio/x-m4a', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/opus',
        'audio/qcp', 'audio/tta', 'audio/voc', 'audio/wav', 'audio/x-ms-wma', 'audio/wv',
        'audio/webm', 'audio/mp3', 'audio/m4a', 'audio/m4b', 'audio/m4p', 'audio/m4r',
        'audio/mpga', 'audio/ogg', 'audio/oga', 'audio/opus', 'audio/qcp', 'audio/tta',
        'audio/voc', 'audio/wav', 'audio/wma', 'audio/wv',
        'video/webm', 'video/mts', 'video/m2ts', 'video/ts', 'video/mov', 'video/mp2',
        'video/mp4', 'video/m4p', 'video/m4v', 'video/mxf'
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a supported audio file format");
        return;
      }

      setUploadedFile(file);
      setUploadProgress(0); // Reset progress
      console.log('File dropped:', file);
    }
  };

  const addSession = async () => {
    console.log('Submit button clicked.');
    console.log('addSession function called.');

    // Immediately disable the button to prevent double submission
    setIsSubmitting(true);

    // Check if a campaign is selected
    if (!selectedCampaign) {
      toast.error('Please select a campaign before uploading.');
      setIsSubmitting(false); // Re-enable button if validation fails
      return;
    }

    // Check if the user can upload
    try {
      const accountInfo = await getAccountInfo(); // Fetch account info
      const usageInfo = accountInfo.usageInfo; // Access usage info from the response
      if (!usageInfo.can_upload) {
        toast.error('You have exceeded your upload limit. Please upgrade your plan.');
        setIsSubmitting(false); // Re-enable button if validation fails
        return;
      }
    } catch (error) {
      toast.error('Failed to verify upload capacity. Please try again later.');
      console.error('Error fetching account info:', error);
      setIsSubmitting(false); // Re-enable button if validation fails
      return;
    }

    const toastId = toast.loading('Uploading and processing audio file...');
    console.log('Starting file upload and processing...');

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('audio', uploadedFile);
      formData.append('campaign_id', selectedCampaign);

      const csrfToken = await fetchCsrfToken();

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/upload/`, true);
      xhr.setRequestHeader('X-CSRFToken', csrfToken);
      xhr.withCredentials = true;
      xhr.timeout = 900000; // 15 minutes in milliseconds

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
          console.log(`Upload progress: ${percentComplete}%`);
          if (percentComplete > 99) {
            setIsProcessing(true);
            console.log('isProcessing set to true after upload progress reached 100%.');
          }
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          if (data.redirect_url) {
            toast.success('Session added successfully! Processing will continue in the background.');
            const transcriptId = new URLSearchParams(data.redirect_url.split('?')[1]).get('transcript_id');
            navigate(`/archive?transcript_id=${transcriptId}`);
          } else {
            console.error('Upload failed - no redirect URL provided');
            toast.error('Upload failed - no redirect URL provided');
          }
        } else if (xhr.status === 413) {
          console.error('File size too large. Please upload a smaller file (max 1GB).');
          toast.error('File size too large. Please upload a smaller file (max 1GB).');
        } else {
          console.error(`Failed to upload audio file. Status: ${xhr.status}, Response: ${xhr.responseText}`);
          toast.error(`Failed to upload audio file. Status: ${xhr.status}`);
        }
        // Cleanup code
        console.log('Upload process completed.');
        toast.dismiss(toastId);
        setUploadedFile(null);
        setUploadProgress(0);
        setIsUploading(false);
        setIsProcessing(false);
        setIsSubmitting(false); // Re-enable button
        console.log('isProcessing set to false.');
      };

      xhr.onerror = () => {
        console.error(`XHR Error - Status: ${xhr.status}, Response: ${xhr.responseText}`);
        toast.error('Failed to upload audio file');
        // Cleanup code
        console.log('Upload process completed.');
        toast.dismiss(toastId);
        setUploadedFile(null);
        setUploadProgress(0);
        setIsUploading(false);
        setIsProcessing(false);
        setIsSubmitting(false); // Re-enable button
        console.log('isProcessing set to false.');
      };

      xhr.send(formData);
    } catch (err) {
      toast.error(err.message || 'Failed to upload audio file');
      console.error('Upload error:', err);
      setIsSubmitting(false); // Re-enable button on error
    }
  };

  return (
    <main
      className="min-h-[calc(100vh-56px)] flex flex-col bg-cover bg-center bg-no-repeat p-6"
      style={{
        backgroundImage: "url('/imgs/dashbaord-bg.jpg')",
      }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-center flex-1">
            Let's Get Started!
          </h1>
          {hasStripeSubscription === false && <UpgradeButton className="ml-4" />}
        </div>

        {/* Campaign selector */}
        <div className="mb-8 max-w-md mx-auto">
          <label className="block text-center mb-4">
            Choose Campaign to Add Session To:
          </label>
          <select
            className="w-full p-3 rounded-lg bg-white text-gray-800 mb-2"
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}>
            <option value="">Select Campaign</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.title}
              </option>
            ))}
          </select>
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full flex items-center justify-center bg-white/10 hover:bg-primary-light space-x-2 text-white rounded-lg p-2">
            <IoAddCircleOutline size={20} />
            <span>Create New Campaign</span>
          </button>
        </div>

        {/* Upload section */}
        <div className="flex-1 flex flex-col">
          <div
            className={`bg-amber-500/20 border-2 border-dashed rounded-lg p-8 text-center flex-1 flex flex-col justify-center transition-colors
              ${
                isDragging
                  ? "border-amber-300 bg-amber-300/50"
                  : "border-amber-500"
              }
              `}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}>
            {isUploading ? (
              <div className="space-y-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-amber-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <p className="text-white">Uploading... {uploadProgress}%</p>
              </div>
            ) : uploadedFile ? (
              <div className="space-y-4">
                <p className="text-white">
                  ✓ File "{uploadedFile.name}" ready for upload
                </p>
                <button
                  onClick={addSession}
                  disabled={isSubmitting}
                  className={`px-4 py-2 rounded-lg ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-primary hover:bg-primary-light'
                  }`}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                <button
                  onClick={() => setUploadedFile(null)}
                  className="flex text-center mx-auto items-center text-red-400 hover:text-red-200">
                  <AiOutlineDelete size={24} /> <span>Remove file</span>
                </button>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  id="fileInput"
                  className="hidden"
                  accept=".3ga,.8svx,.aac,.ac3,.aif,.aiff,.alac,.amr,.ape,.au,.dss,.flac,.flv,.m4a,.m4b,.m4p,.m4r,.mp3,.mpga,.ogg,.oga,.opus,.qcp,.tta,.voc,.wav,.wma,.wv,.webm,.mts,.m2ts,.ts,.mov,.mp2,.mp4,.m4p,.m4v,.mxf"
                  onChange={handleFileSelect}
                />
                <label
                  htmlFor="fileInput"
                  className="cursor-pointer">
                  <button
                    className="bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-lg flex items-center justify-center mx-auto space-x-2"
                    onClick={() => {
                      document.getElementById("fileInput").click();
                    }}>
                    <RxUpload size={24} />
                    <span>Select RPG Session you would like to upload</span>
                  </button>
                </label>
                <p className="text-white mt-4">
                  or drag and drop your file here
                </p>
              </>
            )}
          </div>
        </div>

        {/* Help link */}
        {/* <div className="mt-6 text-center underline underline-offset-4">
          <Link
            to="#"
            className="inline-flex items-center text-white space-x-2">
            <GoQuestion />
            <span className="hover:text-gray-300">
              Need Help? Click Here for Instructions
            </span>
          </Link>
        </div> */}
      </div>

      <CreateCampaignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateCampaign}
      />

      {/* Processing Modal */}
      {isProcessing && (
        <Modal isOpen={isProcessing} onClose={() => {}}>
          <div className="flex flex-col items-center justify-center space-y-4">
            <p className="text-lg font-semibold text-center text-black">
              Upload Complete! Your file is now processing. This may take 3-10 minutes, depending on the length of the session.
            </p>
            <p className="text-sm text-center text-black">
              You will be redirected to the archive page. Processing will continue in the background, and you can check the status there.
            </p>
            <Spinner />
          </div>
        </Modal>
      )}
    </main>
  );
};

export default Dashboard; 